"use strict";
/*
 * Copyright 2024, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFilteredModesPre = exports.getFilteredModes = exports.conditionalHhMayHaveDisability = exports.conditionalPersonMayHaveDisability = exports.shouldShowSameAsReverseTripQuestion = exports.isSimpleChainSingleModeReturnTrip = exports.getPreviousTripSingleSegment = void 0;
const isEqual_1 = __importDefault(require("lodash/isEqual"));
const odHelpers = __importStar(require("../../../odSurvey/helpers"));
const types_1 = require("../../../odSurvey/types");
/**
 * Get the mode used in the single segment of the previous trip of the active
 * one
 *
 * @param {Object} options - The options object.
 * @param {Object} options.interview The interview object
 * @param {Object} options.person The person these trips belong to
 * @returns If there is a single mode in the previous trip, return it, otherwise
 * undefined
 */
const getPreviousTripSingleSegment = ({ interview, person }) => {
    const journey = odHelpers.getActiveJourney({ interview, person });
    const trip = odHelpers.getActiveTrip({ interview, journey });
    if (!journey || !trip) {
        return undefined;
    }
    const previousTrip = odHelpers.getPreviousTrip({ currentTrip: trip, journey });
    if (previousTrip) {
        const previousSegments = odHelpers.getSegmentsArray({ trip: previousTrip });
        if (previousSegments.length === 1) {
            const previousSegment = previousSegments[0];
            return previousSegment;
        }
    }
    return undefined;
};
exports.getPreviousTripSingleSegment = getPreviousTripSingleSegment;
/**
 * Return whether these 2 trips are part of a simple chain with a single mode,
 * ie the origin of the previous trip is the same as the destination of the trip
 * and there is only one segment in the previous trip that is one of the simple
 * modes.
 *
 * @param {Object} options - The options object.
 * @param {Trip} options.trip The potential return trip
 * @param {Trip} options.previousTrip The previous trip that can be part of the
 * chain
 * @param {Object} options.journey The journey object that these trips are part
 * of
 * @param {Object} options.interview The interview object
 * @param {Object} options.person The person these trips belong to
 * @returns Whether the `trip` is the return trip of a simple chain with simple
 * modes
 */
const isSimpleChainSingleModeReturnTrip = ({ trip, previousTrip, journey, interview, person }) => {
    const visitedPlaces = odHelpers.getVisitedPlaces({ journey });
    const origin = odHelpers.getOrigin({ trip, visitedPlaces });
    const destination = odHelpers.getDestination({ trip, visitedPlaces });
    const previousOrigin = odHelpers.getOrigin({ trip: previousTrip, visitedPlaces });
    // If origin or destination is not found, we cannot determine if it is a simple chain
    if (!origin || !destination || !previousOrigin) {
        return false;
    }
    // ignore loop/moving activities:
    if ((origin.activity && types_1.loopActivities.includes(origin.activity)) ||
        (destination.activity && types_1.loopActivities.includes(destination.activity))) {
        return false;
    }
    // If the trip already has more than one segment, it is not a simple chain
    const segments = trip.segments || {};
    const segmentsArray = Object.values(segments);
    if (segmentsArray.length > 1) {
        return false;
    }
    const previousTripOriginGeography = odHelpers.getVisitedPlaceGeography({
        visitedPlace: previousOrigin,
        interview,
        person
    });
    const tripDestinationGeography = odHelpers.getVisitedPlaceGeography({
        visitedPlace: destination,
        interview,
        person
    });
    if (previousTripOriginGeography &&
        tripDestinationGeography &&
        tripDestinationGeography.geometry &&
        previousTripOriginGeography.geometry &&
        (0, isEqual_1.default)(previousTripOriginGeography.geometry.coordinates, tripDestinationGeography.geometry.coordinates)) {
        const previousTripSegmentsAsArray = odHelpers.getSegmentsArray({ trip: previousTrip });
        if (previousTripSegmentsAsArray.length === 1 &&
            previousTripSegmentsAsArray[0].mode &&
            types_1.simpleModes.includes(previousTripSegmentsAsArray[0].mode)) {
            // we have a simple chain with single simple mode
            return true;
        }
    }
    return false;
};
exports.isSimpleChainSingleModeReturnTrip = isSimpleChainSingleModeReturnTrip;
const shouldShowSameAsReverseTripQuestion = ({ interview, segment }) => {
    // Do not display if segment is not new
    if (segment._isNew === false) {
        return false;
    }
    // Display this question if the segment is new and the previous and current
    // trips form a simple chain with a single mode
    const person = odHelpers.getPerson({ interview });
    const journey = odHelpers.getActiveJourney({ interview, person });
    const trip = odHelpers.getActiveTrip({ interview, journey });
    const previousTrip = trip !== null ? odHelpers.getPreviousTrip({ currentTrip: trip, journey }) : null;
    return (trip !== null &&
        previousTrip !== null &&
        (0, exports.isSimpleChainSingleModeReturnTrip)({ interview, journey, person, trip, previousTrip }));
};
exports.shouldShowSameAsReverseTripQuestion = shouldShowSameAsReverseTripQuestion;
const conditionalPersonMayHaveDisability = (interview) => {
    const person = odHelpers.getActivePerson({ interview });
    const personMayHaveDisability = person ? odHelpers.personMayHaveDisability({ person: person }) : true;
    return personMayHaveDisability;
};
exports.conditionalPersonMayHaveDisability = conditionalPersonMayHaveDisability;
const conditionalHhMayHaveDisability = (interview) => odHelpers.householdMayHaveDisability({ interview });
exports.conditionalHhMayHaveDisability = conditionalHhMayHaveDisability;
/**
 * Filter the available modes based on the segment section configuration.
 * If the section is enabled and modesIncludeOnly is set, keep only those modes in the order specified.
 * If the section is enabled and modesExclude is set, exclude those modes.
 */
const getFilteredModes = (segmentConfig = { enabled: true }) => {
    if (segmentConfig.enabled === false) {
        return [];
    }
    if (segmentConfig.modesIncludeOnly) {
        // Keep only modes that exist in both modesIncludeOnly and modeValues, in the order specified in modesIncludeOnly
        return segmentConfig.modesIncludeOnly.filter((mode) => types_1.modeValues.includes(mode));
    }
    if (segmentConfig.modesExclude) {
        // Exclude modes that are in modesExclude
        return types_1.modeValues.filter((mode) => !segmentConfig.modesExclude.includes(mode));
    }
    return types_1.modeValues;
};
exports.getFilteredModes = getFilteredModes;
/**
 * Filter the available mode categories (modePre) based on the filtered modes.
 * Only keep modePre values that have at least one available mode.
 */
const getFilteredModesPre = (availableModes) => {
    // Keep only modePre values that have at least one mode in the availableModes
    return types_1.modePreValues.filter((modePre) => {
        // Get all modes for this modePre from the reverse map
        const modesForThisModePre = Object.entries(types_1.modeToModePreMap)
            .filter(([_mode, modePres]) => modePres.includes(modePre))
            .map(([mode, _modePres]) => mode);
        // Check if at least one of these modes is in the availableModes
        return modesForThisModePre.some((mode) => availableModes.includes(mode));
    });
};
exports.getFilteredModesPre = getFilteredModesPre;
//# sourceMappingURL=helpers.js.map